/**
 * FAIRy Dashboard Panel Component
 *
 * Portfolio-level FAIRy interface for the journey dashboard.
 * Displays portfolio summary, proactive suggestions, and chat interface.
 *
 * @module client/components/FairyDashboardPanel
 * @version 1.0
 * @date 2025-10-27
 */

import React, { useState, useEffect, useRef } from 'react';
import { BRANDING } from '../config/branding';
import type {
  DashboardContext,
  ProactivePrompt,
  ChatQueryResponse
} from '../../server/api/types/fairy';

interface FairyDashboardPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'fairy';
  text: string;
  timestamp: Date;
  suggestions?: string[];
  related_journeys?: string[];
}

export const FairyDashboardPanel: React.FC<FairyDashboardPanelProps> = ({ isOpen, onClose }) => {
  const [context, setContext] = useState<DashboardContext | null>(null);
  const [prompts, setPrompts] = useState<ProactivePrompt[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Load dashboard context when panel opens
  useEffect(() => {
    if (isOpen && !context) {
      loadDashboardContext();
    }
  }, [isOpen, context]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  /**
   * Load dashboard context from API
   */
  const loadDashboardContext = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/fairy/dashboard-context');

      if (!response.ok) {
        throw new Error(`Failed to load context: ${response.statusText}`);
      }

      const data = await response.json();
      setContext(data);
      setPrompts(data.proactive_prompts || []);

      // Add welcome message from FAIRy
      if (data.user?.behavior?.is_first_time) {
        setChatMessages([
          {
            id: Date.now().toString(),
            type: 'fairy',
            text: `Welcome to ${BRANDING.productName}! I'm FAIRy, your AI copilot. I can help you understand your FAIR journey portfolio, identify patterns, and suggest improvements. What would you like to know?`,
            timestamp: new Date()
          }
        ]);
      } else {
        setChatMessages([
          {
            id: Date.now().toString(),
            type: 'fairy',
            text: `Welcome back! You have ${data.portfolio?.stats?.total_journeys || 0} journeys. How can I help you today?`,
            timestamp: new Date()
          }
        ]);
      }
    } catch (err: unknown) {
      console.error('[FAIRy Dashboard] Failed to load context:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send chat message to API
   */
  const sendChatMessage = async () => {
    if (!chatInput.trim() || !context) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: chatInput,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/fairy/dashboard-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: chatInput,
          context
        })
      });

      if (!response.ok) {
        throw new Error(`Chat failed: ${response.statusText}`);
      }

      const data: ChatQueryResponse = await response.json();

      const fairyMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'fairy',
        text: data.answer,
        timestamp: new Date(),
        suggestions: data.proactive_suggestions,
        related_journeys: data.related_journeys
      };

      setChatMessages(prev => [...prev, fairyMessage]);
    } catch (err: unknown) {
      console.error('[FAIRy Dashboard] Chat failed:', err);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'fairy',
        text: `Sorry, I encountered an error: ${err instanceof Error ? err.message : String(err)}. Please try again.`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Dismiss a proactive prompt
   */
  const dismissPrompt = async (promptId: string) => {
    try {
      const response = await fetch('/api/fairy/dismiss-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt_id: promptId })
      });

      if (response.ok) {
        setPrompts(prev => prev.filter(p => p.id !== promptId));
      }
    } catch (err) {
      console.error('[FAIRy Dashboard] Failed to dismiss prompt:', err);
    }
  };

  /**
   * Handle suggestion click
   */
  const handleSuggestionClick = (suggestion: string) => {
    setChatInput(suggestion);
  };

  /**
   * Handle prompt action (CTA button)
   */
  const handlePromptAction = (action: string, journey_id?: string) => {
    // TODO: Implement action handlers
    console.log('[FAIRy Dashboard] Prompt action:', action, journey_id);

    // For now, just dismiss the prompt
    if (journey_id) {
      // Navigate to journey or show relevant UI
      window.location.href = `/journey/${journey_id}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fairy-dashboard-panel-overlay" onClick={onClose}>
      <div className="fairy-dashboard-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="fairy-header">
          <h3>FAIRy Insights</h3>
          <span className="fairy-mode-badge">Portfolio Mode</span>
          <button className="fairy-close-btn" onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>

        {/* Loading State */}
        {loading && !context && (
          <div className="fairy-loading">
            <div className="spinner"></div>
            <p>Loading your portfolio...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="fairy-error">
            <p>⚠️ {error}</p>
            <button className="btn btn-sm btn-secondary" onClick={loadDashboardContext}>
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {context && (
          <>
            {/* Portfolio Summary */}
            <div className="fairy-portfolio-summary">
              <h4>Your FAIR Journey Portfolio</h4>
              <div className="portfolio-stats">
                <div className="stat-card">
                  <div className="stat-value">{context.portfolio.stats.total_journeys}</div>
                  <div className="stat-label">Total Journeys</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{context.portfolio.stats.avg_fair_score}%</div>
                  <div className="stat-label">Avg FAIR Score</div>
                  {context.portfolio.patterns.improvement_trend === 'up' && (
                    <div className="stat-trend up">+{context.portfolio.patterns.fair_score_delta}% trend</div>
                  )}
                  {context.portfolio.patterns.improvement_trend === 'down' && (
                    <div className="stat-trend down">{context.portfolio.patterns.fair_score_delta}% trend</div>
                  )}
                </div>
                <div className="stat-card">
                  <div className="stat-value">{context.portfolio.stats.completed}</div>
                  <div className="stat-label">Completed</div>
                </div>
                {context.portfolio.stats.abandoned > 0 && (
                  <div className="stat-card stat-warning">
                    <div className="stat-value">{context.portfolio.stats.abandoned}</div>
                    <div className="stat-label">Abandoned</div>
                  </div>
                )}
              </div>
            </div>

            {/* Proactive Suggestions */}
            {prompts.length > 0 && (
              <div className="fairy-suggestions">
                <h4>Suggested Actions</h4>
                {prompts.map(prompt => (
                  <div key={prompt.id} className={`suggestion-card priority-${prompt.priority}`}>
                    <div className="suggestion-icon">
                      {prompt.type === 'first_time' && '👋'}
                      {prompt.type === 'abandoned' && '🔄'}
                      {prompt.type === 'milestone' && '🎉'}
                      {prompt.type === 'trend' && '📈'}
                      {prompt.type === 'blocker' && '⚠️'}
                    </div>
                    <div className="suggestion-content">
                      <div className="suggestion-title">{prompt.title}</div>
                      <div className="suggestion-description">{prompt.message}</div>
                    </div>
                    <div className="suggestion-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handlePromptAction(prompt.cta_action, prompt.related_journey_ids?.[0])}
                      >
                        {prompt.cta_label}
                      </button>
                      {prompt.dismissible && (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => dismissPrompt(prompt.id)}
                          aria-label="Dismiss"
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Chat Interface */}
            <div className="fairy-chat">
              <h4>Ask FAIRy</h4>
              <div className="fairy-chat-messages">
                {chatMessages.map(msg => (
                  <div key={msg.id} className={`chat-message ${msg.type}`}>
                    <div className="message-avatar">
                      {msg.type === 'fairy' ? '✨' : '👤'}
                    </div>
                    <div className="message-content">
                      <div className="message-text">{msg.text}</div>
                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <div className="message-suggestions">
                          {msg.suggestions.map((sug, idx) => (
                            <button
                              key={idx}
                              className="suggestion-chip"
                              onClick={() => handleSuggestionClick(sug)}
                            >
                              {sug}
                            </button>
                          ))}
                        </div>
                      )}
                      {msg.related_journeys && msg.related_journeys.length > 0 && (
                        <div className="message-related">
                          <small>Related journeys: {msg.related_journeys.length}</small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form
                className="fairy-chat-input"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendChatMessage();
                }}
              >
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask about your journeys..."
                  disabled={loading}
                />
                <button type="submit" disabled={loading || !chatInput.trim()}>
                  {loading ? '...' : 'Send'}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FairyDashboardPanel;
